import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

import { useI18n } from '../../../../../i18n';
import {
  fetchDocumento,
  fetchVersiones,
  actualizarContenido,
  BASE,
  getToken,
} from '../../../../shared/services/api';
import type { Documento, DocumentVersion, EstadoDocumento } from '../../../../../types';
import { exportViewerToPdf } from '../../../../../lib/exportPdf';
import { renderContentBlocks } from '../viewer';
import type { ContentBlock } from '../viewer';
import { HeadingNumbering } from '../editor/extensions/HeadingNumbering';
import { numberHeadings, renderContent } from './document-renderers';

type PanelTab = 'contenido' | 'versiones' | 'referencias' | 'archivos';

interface PanelHandlers {
  selectVersion: (index: number) => void;
  enterEditMode: () => void;
  exitEditMode: () => Promise<void>;
  discardChanges: () => Promise<void>;
  manualSave: () => Promise<void>;
  exportPdf: () => Promise<void>;
}

interface PanelState {
  versions: DocumentVersion[];
  selectedVersionIndex: number;
  activeTab: PanelTab;
  doc: Documento | null;
  isEditing: boolean;
  saving: boolean;
  canEdit: boolean;
  lastSaved: Date | null;
  handlers: PanelHandlers;
}

interface UseDocumentPanelArgs {
  documentoCodigo: string | null;
  editingFromParent?: boolean;
  onEditingChange?: (editing: boolean) => void;
  onExportRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onStateChange?: (state: PanelState) => void;
  useContentPagesMode: boolean;
  externalZoom?: number;
  onZoomChangeProp?: (zoom: number) => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

export function useDocumentPanel({
  documentoCodigo,
  editingFromParent,
  onEditingChange,
  onExportRef,
  onStateChange,
  useContentPagesMode,
  externalZoom,
  onZoomChangeProp,
  panelRef,
}: UseDocumentPanelArgs) {
  const { t } = useI18n();

  const [doc, setDoc] = useState<Documento | null>(null);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PanelTab>('contenido');

  const [showVIA, setShowVIA] = useState(false);
  const [showImportDocx, setShowImportDocx] = useState(false);

  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const isEditing = editingFromParent !== undefined ? editingFromParent : isEditingInternal;
  const setIsEditing = useCallback(
    (v: boolean) => {
      setIsEditingInternal(v);
      onEditingChange?.(v);
    },
    [onEditingChange],
  );

  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const autoSaveRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const [zoom, setZoomInternal] = useState(externalZoom ?? 100);
  const setZoom = useCallback(
    (v: number | ((prev: number) => number)) => {
      setZoomInternal((prev) => {
        const next = typeof v === 'function' ? v(prev) : v;
        onZoomChangeProp?.(next);
        return next;
      });
    },
    [onZoomChangeProp],
  );
  useEffect(() => {
    if (externalZoom != null) setZoomInternal(externalZoom);
  }, [externalZoom]);
  const zoomIn = useCallback(() => setZoom((z) => Math.min(z + 10, 200)), [setZoom]);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(z - 10, 30)), [setZoom]);

  const [contentBlocksA4, setContentBlocksA4] = useState<ContentBlock[]>([]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      HeadingNumbering,
    ],
    content: null,
    editable: false,
  });

  const selectedVersion = versions[selectedVersionIndex] ?? null;
  const estado = (doc?.estado ?? 'BORRADOR') as EstadoDocumento;
  const borradorVersion = versions.find(
    (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
  );

  const selectedVersionEstado = (selectedVersion?.estado ?? estado) as EstadoDocumento;
  const isSelectedVersionVigente =
    selectedVersionEstado === 'PUBLICADO' ||
    (selectedVersionEstado === 'APROBADO' &&
      (doc?.estado === 'PUBLICADO' || doc?.estado === 'APROBADO'));
  const isSelectedVersionBorrador =
    selectedVersionEstado === 'BORRADOR' || (selectedVersionEstado as string) === 'EN_REVISION';

  const displayEstado = isSelectedVersionVigente
    ? ('PUBLICADO' as EstadoDocumento)
    : selectedVersionEstado;

  const canEdit = !!borradorVersion && isSelectedVersionBorrador;

  const findVigenteIndex = useCallback((vers: DocumentVersion[], docData: Documento | null) => {
    if (!vers.length) return 0;
    const vigenteId = (docData as any)?.versionVigenteId;
    if (!vigenteId) return 0;
    const idx = vers.findIndex((v) => v.id === vigenteId);
    return idx >= 0 ? idx : 0;
  }, []);

  const showToast = useCallback((msg: string, duration = 3000) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  }, []);

  const handlersRef = useRef<PanelHandlers>({
    selectVersion: () => {},
    enterEditMode: () => {},
    exitEditMode: async () => {},
    discardChanges: async () => {},
    manualSave: async () => {},
    exportPdf: async () => {},
  });

  useEffect(() => {
    if (editingFromParent !== undefined && editor) {
      editor.setEditable(editingFromParent);
      if (!editingFromParent && selectedVersion) {
        actualizarContenido(selectedVersion.id, editor.getJSON()).catch(() => {});
      }
    }
  }, [editingFromParent]);

  useEffect(() => {
    if (!documentoCodigo) {
      setDoc(null);
      setVersions([]);
      setSelectedVersionIndex(0);
      setError(null);
      setIsEditing(false);
      setActiveTab('contenido');
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setIsEditing(false);
    setActiveTab('contenido');

    Promise.all([fetchDocumento(documentoCodigo), fetchVersiones(documentoCodigo)])
      .then(([docRes, verRes]) => {
        if (cancelled) return;
        setDoc(docRes.data);
        const vers = verRes.data ?? [];
        setVersions(vers);
        const vigIdx = findVigenteIndex(vers, docRes.data);
        setSelectedVersionIndex(vigIdx);
        const versionToShow = vers[vigIdx] ?? null;
        if (editor && versionToShow?.contenidoJson) {
          editor.commands.setContent(versionToShow.contenidoJson);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? t.common.errorCargarDocumento);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [documentoCodigo, editor]);

  useEffect(() => {
    if (!versions.length || isEditing) return;
    const version = versions[selectedVersionIndex] ?? null;
    if (editor && version?.contenidoJson) {
      editor.commands.setContent(version.contenidoJson);
    }
  }, [selectedVersionIndex, versions, isEditing, editor]);

  const doSave = useCallback(async () => {
    if (!editor || !selectedVersion) return;
    const editVersion = borradorVersion ?? selectedVersion;
    setSaving(true);
    try {
      await actualizarContenido(editVersion.id, editor.getJSON());
      setLastSaved(new Date());
    } catch {
      // silently fail auto-save
    } finally {
      setSaving(false);
    }
  }, [editor, selectedVersion, borradorVersion]);

  useEffect(() => {
    if (isEditing) {
      autoSaveRef.current = setInterval(doSave, 30000);
    } else if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current);
      autoSaveRef.current = null;
    }
    return () => {
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [isEditing, doSave]);

  const enterEditMode = useCallback(() => {
    if (!editor || !versions.length) return;
    const editVersionIdx = versions.findIndex(
      (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
    );
    const editVersion = editVersionIdx >= 0 ? versions[editVersionIdx] : versions[0];
    if (editVersionIdx >= 0) setSelectedVersionIndex(editVersionIdx);
    editor.setEditable(true);
    editor.commands.setContent(editVersion.contenidoJson);
    setIsEditing(true);
    setActiveTab('contenido');
  }, [editor, versions, setIsEditing]);

  const exitEditMode = useCallback(async () => {
    if (!editor) return;
    const editVersion = borradorVersion ?? selectedVersion;
    if (editVersion) {
      setSaving(true);
      try {
        await actualizarContenido(editVersion.id, editor.getJSON());
        setLastSaved(new Date());
      } catch {
        /* ignore */
      } finally {
        setSaving(false);
      }
    }
    editor.setEditable(false);
    setIsEditing(false);
    if (documentoCodigo) {
      try {
        const verRes = await fetchVersiones(documentoCodigo);
        setVersions(verRes.data);
      } catch {
        /* ignore */
      }
    }
  }, [editor, borradorVersion, selectedVersion, documentoCodigo, setIsEditing]);

  const handleManualSave = useCallback(async () => {
    if (!editor) return;
    const editVersion = borradorVersion ?? selectedVersion;
    if (!editVersion) return;
    setSaving(true);
    try {
      await actualizarContenido(editVersion.id, editor.getJSON());
      setLastSaved(new Date());
      showToast(t.common.documentSaved);
    } catch (err: any) {
      showToast(`${t.common.errorSaving}${err.message ?? t.common.errorDesconocido}`, 4000);
    } finally {
      setSaving(false);
    }
  }, [editor, borradorVersion, selectedVersion, showToast]);

  const discardChanges = useCallback(async () => {
    if (!documentoCodigo || !editor) return;
    try {
      const verRes = await fetchVersiones(documentoCodigo);
      setVersions(verRes.data);
      const editVersion =
        verRes.data.find(
          (v) => v.estado === 'BORRADOR' || (v.estado as string) === 'EN_REVISION',
        ) ?? verRes.data[0];
      if (editVersion?.contenidoJson) {
        editor.commands.setContent(editVersion.contenidoJson);
      }
      editor.setEditable(false);
      setIsEditing(false);
      setDiscardConfirmOpen(false);
      showToast(t.common.changesDiscarded);
    } catch (err: any) {
      showToast(err.message || t.common.errorDescartar, 4000);
    }
  }, [documentoCodigo, editor, setIsEditing, showToast]);

  const handleSelectVersion = useCallback(
    (index: number) => {
      if (isEditing) return;
      setSelectedVersionIndex(index);
      setActiveTab('contenido');
    },
    [isEditing],
  );

  const contentHtml = useMemo(() => {
    if (!selectedVersion?.contenidoJson) return '';
    const numbered = numberHeadings(selectedVersion.contenidoJson);
    return renderContent(numbered);
  }, [selectedVersion?.contenidoJson]);

  useEffect(() => {
    if (!useContentPagesMode || !selectedVersion?.contenidoJson) {
      setContentBlocksA4([]);
      return;
    }
    const numbered = numberHeadings(selectedVersion.contenidoJson);
    setContentBlocksA4(renderContentBlocks(numbered));
  }, [useContentPagesMode, selectedVersion?.contenidoJson]);

  const editionLabel = selectedVersion
    ? `Ed. ${String(selectedVersion.numeroEdicion).padStart(2, '0')}`
    : '';
  const dateLabel = selectedVersion?.actualizadoEn
    ? new Date(selectedVersion.actualizadoEn).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  const handleExportPdf = useCallback(async () => {
    if (!doc || !panelRef.current) return;
    const filename = `${doc.codigo} ${doc.nombre}.pdf`;
    await exportViewerToPdf(panelRef.current, filename);
  }, [doc]);

  const [exportingWord, setExportingWord] = useState(false);
  const handleExportWord = useCallback(async () => {
    if (!doc) return;
    setExportingWord(true);
    try {
      // Descarga binaria: fetch manual con Bearer (request() parsea JSON).
      const token = getToken();
      const res = await fetch(
        `${BASE}/documentos/${encodeURIComponent(doc.codigo)}/exportar/word`,
        {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: 'Error al exportar' } }));
        throw new Error(err.error?.message ?? `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.codigo} ${doc.nombre}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      showToast(`Error: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    } finally {
      setExportingWord(false);
    }
  }, [doc, showToast]);

  useEffect(() => {
    if (onExportRef) onExportRef.current = handleExportPdf;
    return () => {
      if (onExportRef) onExportRef.current = null;
    };
  }, [handleExportPdf, onExportRef]);

  useEffect(() => {
    handlersRef.current = {
      selectVersion: handleSelectVersion,
      enterEditMode,
      exitEditMode,
      discardChanges,
      manualSave: handleManualSave,
      exportPdf: handleExportPdf,
    };

    onStateChange?.({
      versions,
      selectedVersionIndex,
      activeTab,
      doc,
      isEditing,
      saving,
      canEdit,
      lastSaved,
      handlers: handlersRef.current,
    });
  }, [
    versions,
    selectedVersionIndex,
    activeTab,
    doc,
    isEditing,
    saving,
    canEdit,
    lastSaved,
    handleSelectVersion,
    enterEditMode,
    exitEditMode,
    discardChanges,
    handleManualSave,
    handleExportPdf,
  ]);

  const refCount = (doc?.referenciasSalida?.length ?? 0) + (doc?.referenciasEntrada?.length ?? 0);

  return {
    doc,
    versions,
    selectedVersionIndex,
    setSelectedVersionIndex,
    loading,
    error,
    activeTab,
    setActiveTab,
    showVIA,
    setShowVIA,
    showImportDocx,
    setShowImportDocx,
    isEditing,
    saving,
    lastSaved,
    toast,
    discardConfirmOpen,
    setDiscardConfirmOpen,
    zoom,
    zoomIn,
    zoomOut,
    contentBlocksA4,
    editor,
    selectedVersion,
    estado,
    borradorVersion,
    isSelectedVersionVigente,
    isSelectedVersionBorrador,
    displayEstado,
    canEdit,
    contentHtml,
    editionLabel,
    dateLabel,
    exportingWord,
    refCount,
    enterEditMode,
    exitEditMode,
    handleManualSave,
    discardChanges,
    handleSelectVersion,
    handleExportPdf,
    handleExportWord,
  };
}
